import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Coins, Sparkles, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function BuyRunegoldModal({ open, onOpenChange, reason }) {
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get('/wallet/packages')
      .then((r) => setPackages(r.data || []))
      .catch(() => toast.error('Failed to load Runegold packages'));
  }, [open]);

  const handlePurchase = async (pkg) => {
    setSelectedPkg(pkg.id);
    setLoading(true);
    try {
      const res = await api.post('/wallet/purchase', {
        package_id: pkg.id,
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error('Unable to start checkout');
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Purchase unavailable';
      toast.error(msg);
    } finally {
      setLoading(false);
      setSelectedPkg(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="buy-runegold-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-yellow-500" />
            Buy Runegold
          </DialogTitle>
        </DialogHeader>

        {reason && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-muted/50 text-sm text-muted-foreground" data-testid="buy-runegold-reason">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-yellow-500" />
            <span>{reason}</span>
          </div>
        )}

        <div className="flex items-center justify-between px-1 text-sm text-muted-foreground">
          <span>Your balance</span>
          <span className="font-bold text-foreground">{user?.wonder_coins?.toFixed(0) || 0} Runegold</span>
        </div>

        <div className="space-y-3 mt-1">
          {packages.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => handlePurchase(pkg)}
              disabled={loading}
              className={`w-full flex items-center justify-between p-4 rounded-xl border transition-colors text-left ${
                pkg.popular
                  ? 'border-primary bg-primary/5 hover:bg-primary/10'
                  : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
              data-testid={`runegold-package-${pkg.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  pkg.popular ? 'bg-primary/20' : 'bg-yellow-500/10'
                }`}>
                  <Coins className={`w-5 h-5 ${pkg.popular ? 'text-primary' : 'text-yellow-500'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{pkg.name}</span>
                    {pkg.popular && (
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-primary text-white flex items-center gap-0.5">
                        <Sparkles className="w-2.5 h-2.5" /> Popular
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground">{pkg.coins} Runegold</span>
                </div>
              </div>
              <div className="text-right">
                <span className="font-bold">${pkg.price.toFixed(2)}</span>
                {loading && selectedPkg === pkg.id && (
                  <span className="block text-xs text-muted-foreground animate-pulse">Processing…</span>
                )}
              </div>
            </button>
          ))}
        </div>

        <p className="text-[11px] text-muted-foreground text-center mt-1 flex items-center justify-center gap-1">
          <ExternalLink className="w-3 h-3" /> Secure payment via Stripe
        </p>
      </DialogContent>
    </Dialog>
  );
}
